import React from "react";
import { connect } from "react-redux";
import * as R from "ramda";
import moment from "moment";
import { Div, InfoIcon, Span } from "components/Base";
import {
  FIELD_IDS,
  MODULES_TINY_MCE,
  FORMATS_TINY_MCE
} from "EventLight/Sessions/AddSessionModal/constants";
import { Link } from "react-router";
import { setLocalZone } from "utils/General";
import { actions, getters } from "EventLight/Sessions/AddSessionModal/model";
import {
  getEventTimezone,
  eventId as getEventId
} from "redux/modules/event/selectors";
import { Panel } from "EventLight/Common/FieldLayout";
import {
  Input,
  DateTimeInput,
  AdvancedWYSIWYG,
  DropdownAsync
} from "ui-kit/Form/View";
import { canEditSessions } from "../selectors";

import { makeStyles } from "@material-ui/core/styles";

import CanUserDo from "components/Global/Security/CanUserDo";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

import MultiValueLabel from "./DecorateMultiValueLabel";
import Option from "EventLight/Sessions/Page/View/OptionSpeakerDropdown";

const useStyles = makeStyles(() => ({
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignContent: "center",
    justifyContent: "space-between",
    alignItems: "baseline",
    maxWidth: "100%",
    gap: 25,
    marginBottom: 15
  },
  fieldWidth1_1: {
    width: `100%`
  },
  fieldWidth1_2: {
    width: `50%`
  },
  fieldWidth1_3: {
    width: `30%`
  }
}));

const InputWrapperRow = ({ children, ...props }) => {
  return (
    <Div display="row" {...props}>
      {children}
    </Div>
  );
};

const Label = ({ children, tooltip, style = {} }) => {
  return (
    <Div
      color="black"
      fw={3}
      fs={3}
      display="row.flex-start.center"
      style={style}
      mb={2}
    >
      {children}
      {tooltip ? <InfoIcon ml={1} color="gray5" tooltip={tooltip} /> : null}
    </Div>
  );
};

const customStyles = {
  menuList: styles => ({
    ...styles,
    zIndex: 9999
  }),
  menu: styles => ({ ...styles, zIndex: 9999 })
};

const decorate = connect(
  state => ({
    data: getters.data(state),
    timezone: getEventTimezone(state),
    eventId: getEventId(state),
    parentData: getters.parentSessionData(state),
    canEdit: canEditSessions(state)
  }),
  {
    uploadHeaderImg: actions.uploadHeaderImg,
    toggleOnlineSession: actions.toggleOnlineSession,
    removeContent: actions.removeContent,
    reorderContent: actions.reorderContent,
    showContentModal: actions.showContentModal,
    removeContentVideo: actions.removeContentVideo
  }
);

const BasicSubsessionDetails = ({
  data,
  timezone,
  eventId,
  parentData,
  canEdit
}) => {
  const classes = useStyles();
  const minDate = R.propOr(undefined, "starts_at", parentData);
  const maxDate = R.propOr(undefined, "ends_at", parentData);

  return (
    <Panel styles={{ marginBottom: 0 }}>
      <div className={classes.row}>
        {data.friendly_id && (
          <div className={classes.section} style={{ width: "15%" }}>
            <Label tooltip="Use this ID to reference your sessions">
              Session ID
            </Label>
            <InputWrapperRow>
              <Label>{data.friendly_id}</Label>
            </InputWrapperRow>
          </div>
        )}

        {/* title */}
        <div className={classes.section} style={{ width: "85%" }}>
          <Label>
            Title <span style={{ color: "red" }}>*</span>
          </Label>
          <Input
            fieldId={FIELD_IDS.TITLE}
            style={{
              width: "100%"
            }}
            iniValue={R.prop(FIELD_IDS.TITLE)(data)}
            size="big"
            inputType="outline"
            placeholder="Enter a title for your subsession"
            disabled={!canEdit}
          />
        </div>
      </div>
      <div className={classes.row}>
        {/* date */}
        <div className={classes.section} style={{ width: "100%" }}>
          <InputWrapperRow display="column">
            <Div display="row.flex-start.center">
              <Div
                style={{
                  width: "33%"
                }}
              >
                <Label
                  tooltip={
                    !minDate && !maxDate
                      ? "You must set the parent session Starts At value before setting this value"
                      : `Must be after the parent session begins: ${moment(
                          setLocalZone(moment(minDate), timezone, true)
                        ).format("MMMM D, YYYY, hh:mm A")}`
                  }
                >
                  Starts At
                </Label>
                <DateTimeInput
                  fieldId={FIELD_IDS.STARTS_AT}
                  iniValue={setLocalZone(
                    R.propOr(null, FIELD_IDS.STARTS_AT, data),
                    timezone,
                    true
                  )}
                  materialUI
                  errorMessage={`The start time should be after the parent session begins (${moment(
                    setLocalZone(moment(minDate), timezone, true)
                  ).format("MMMM D, YYYY, hh:mm A")})`}
                  modalVariant
                  minDate={
                    minDate &&
                    setLocalZone(
                      moment(minDate)
                        .seconds(0)
                        .milliseconds(0),
                      timezone,
                      true
                    )
                  }
                  maxDate={
                    maxDate &&
                    setLocalZone(
                      moment(maxDate)
                        .seconds(0)
                        .milliseconds(0),
                      timezone,
                      true
                    )
                  }
                  disabled={!canEdit || (!minDate && !maxDate)}
                />
              </Div>

              <Div
                ml={2}
                style={{
                  width: "33%"
                }}
              >
                <Label
                  tooltip={
                    !minDate && !maxDate
                      ? "You must set the parent session Ends At value before setting this value"
                      : `Must be before the parent session ends: ${moment(
                          setLocalZone(moment(maxDate), timezone, true)
                        ).format("MMM D, h:mm A")}`
                  }
                >
                  Ends At
                </Label>
                <DateTimeInput
                  fieldId={FIELD_IDS.ENDS_AT}
                  iniValue={setLocalZone(
                    R.propOr(null, FIELD_IDS.ENDS_AT, data),
                    timezone,
                    true
                  )}
                  materialUI
                  errorMessage={`The end time should be before the parent session ends (${moment(
                    setLocalZone(moment(maxDate), timezone, true)
                  ).format("MMM D, h:mm A")})`}
                  modalVariant
                  minDate={
                    minDate && setLocalZone(moment(minDate), timezone, true)
                  }
                  maxDate={
                    maxDate && setLocalZone(moment(maxDate), timezone, true)
                  }
                  disabled={!canEdit || (!minDate && !maxDate)}
                />
              </Div>

              <Div
                display="column"
                ml={2}
                style={{
                  width: "33%"
                }}
              >
                <Label tooltip="Formats indicate the type, style, and length of the session">
                  Format
                </Label>
                <InputWrapperRow display="column">
                  <DropdownAsync
                    fieldId={FIELD_IDS.FORMAT}
                    iniValue={R.prop(FIELD_IDS.FORMAT)(data)}
                    multipleSelect={false}
                    menuHeight={150}
                    style={{ width: "100%" }}
                    placeholder="Select a format..."
                    loadOptionsOnFocus={true}
                    customStyles={customStyles}
                    disabled={!canEdit}
                  />
                </InputWrapperRow>
                <CanUserDo
                  action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
                >
                  <Link
                    to={`/event-light/${eventId}/sessions/settings/formats`}
                    target="_blank"
                  >
                    <Span color="primary7">Manage your formats</Span>
                  </Link>
                </CanUserDo>
              </Div>
            </Div>
          </InputWrapperRow>
        </div>
      </div>

      <div className={classes.row}>
        {/* description */}
        <div className={classes.section} style={{ width: "100%" }}>
          <Label>Description</Label>

          <InputWrapperRow bg="white" display="column">
            <AdvancedWYSIWYG
              fieldId={FIELD_IDS.DESCRIPTION}
              iniValue={R.propOr("", FIELD_IDS.DESCRIPTION, data)}
              settings={{ height: 350 }}
              modules={MODULES_TINY_MCE}
              formats={FORMATS_TINY_MCE}
              disabled={!canEdit}
            />
          </InputWrapperRow>
        </div>
      </div>

      <div className={classes.row}>
        {/* speakers */}
        <div className={classes.fieldWidth1_1}>
          <Div mt={2} fs={1} display="row.space-between.center">
            <Label tooltip="Select one or more speakers for this session.">
              Speakers
            </Label>
            <CanUserDo
              action={`${STANDARD_MODULE_IDS.sessionSettings.id}_read`}
            >
              <Link to={`/event-light/${eventId}/speakers`} target="_blank">
                <Span color="primary7">Manage your speakers</Span>
              </Link>
            </CanUserDo>
          </Div>
          <InputWrapperRow display="column">
            <DropdownAsync
              fieldId={FIELD_IDS.SPEAKERS}
              iniValue={R.prop(FIELD_IDS.SPEAKERS)(data)}
              multipleSelect={true}
              menuHeight={250}
              style={{ width: "100%" }}
              placeholder="Select one or more speakers..."
              loadOptionsOnFocus={true}
              disabled={!canEdit}
              menuPlacement="top"
              components={{
                Option,
                MultiValueLabel
              }}
            />
          </InputWrapperRow>
        </div>
      </div>
    </Panel>
  );
};
export default decorate(BasicSubsessionDetails);
